// Copyright (c) 2026 Isolated Networks. All rights reserved.
// Proprietary and confidential. Unauthorized copying, distribution, or use
// of this file, via any medium, is strictly prohibited.
// SPDX-License-Identifier: LicenseRef-Proprietary

import React, { useState, useEffect, useRef } from 'react';
import { MqttActivityProvider, useMqttActivity } from '../context/MqttActivityContext';
import { useNavigate } from 'react-router-dom';
import axios from '../utils/axiosConfig';
import { countBatchClients } from '../utils/batchClientCount';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Checkbox from '@mui/material/Checkbox';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import FlagIcon from '@mui/icons-material/Flag';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import {
  pageRootSx,
  pageHeaderSx,
  pageHeaderActionsSx,
  wideTableContainerSx,
  wideTableSx,
} from '../theme/responsiveSx';
import PeopleIcon from '@mui/icons-material/People';
import DeveloperBoardIcon from '@mui/icons-material/DeveloperBoard';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { isDeviceOnline } from '../utils/healthStatus';

// Country flags data
const countryFlags = [
  { code: 'us', name: 'United States' },
  { code: 'gb', name: 'United Kingdom' },
  { code: 'ca', name: 'Canada' },
  { code: 'au', name: 'Australia' },
  { code: 'de', name: 'Germany' },
  { code: 'fr', name: 'France' },
  { code: 'jp', name: 'Japan' },
  { code: 'cn', name: 'China' },
  { code: 'in', name: 'India' },
  { code: 'br', name: 'Brazil' },
  { code: 'ru', name: 'Russia' },
  { code: 'za', name: 'South Africa' },
  { code: 'mx', name: 'Mexico' },
  { code: 'sg', name: 'Singapore' },
  { code: 'ae', name: 'United Arab Emirates' },
];

const RegionsPageInner = () => {
  // Use MQTT activity context for real-time status updates.
  // The periodic tick in MqttActivityContext (every 5s) handles UI refreshes -
  // no need for an additional forced re-render on every serialToDeviceIdMap change,
  // which was causing excessive rendering on every MQTT message.
  const { getStargateStatusByIp, stargateStatusMap, serialToDeviceIdMap, setStargateList } =
    useMqttActivity();

  const { isAdmin } = useAuth();
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const [regions, setRegions] = useState([]);
  const [stargates, setStargates] = useState([]);
  const [edgeDevices, setEdgeDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Debug: Log stargate statuses on each render
  useEffect(() => {
    if (stargates.length > 0) {
      // console.log('[RegionsPage] Checking statuses for', stargates.length, 'stargates');
      stargates.forEach((sg) => {
        const status = sg.status;
        //         console.log(\  - \ (IP: \): \);
      });
    }
  }, [stargates, getStargateStatusByIp]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingRegion, setEditingRegion] = useState(null);
  const [regionName, setRegionName] = useState('');
  const [countryFlag, setCountryFlag] = useState('');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [regionToDelete, setRegionToDelete] = useState(null);

  // Client counts per stargate id. Seed from session so a remount does not
  // flash a spinner / 0 over a count we already had.
  const [clientCounts, setClientCounts] = useState(() => {
    try {
      const raw = sessionStorage.getItem('regionClientCounts');
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  });
  const [clientCountsLoading, setClientCountsLoading] = useState(false);

  // Batch deletion state
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [batchDeleteConfirmOpen, setBatchDeleteConfirmOpen] = useState(false);

  // Requests canceled by the axios interceptor while logging out
  // (CanceledError('Not authenticated')) are expected — not errors.
  const isCanceled = (err) => err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED';

  // Fetch regions
  const fetchRegions = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/regions');
      // The backend returns regions in Postgres heap order (no ORDER BY), which
      // shifts between requests. Sort by name (case-insensitive, id tie-break) so
      // the rows are stable across visits/refreshes.
      const sorted = [...(response.data || [])].sort(
        (a, b) =>
          (a.name || '').localeCompare(b.name || '', undefined, {
            sensitivity: 'base',
          }) || a.id - b.id
      );
      setRegions(sorted);
      setError('');
    } catch (err) {
      if (!isCanceled(err)) {
        setError('Failed to load regions. Please try again later.');
        console.error('Error fetching regions:', err);
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch all stargates - use a reasonable limit instead of 10000
  const fetchAllStargates = async () => {
    try {
      const response = await axios.get('/stargates');
      const stargateData = response.data;
      setStargates(stargateData);

      // Share the stargate list with MQTT context so it can correlate activity data
      setStargateList(stargateData);
    } catch (err) {
      if (!isCanceled(err)) {
        console.error('Error fetching stargates:', err);
      }
    }
  };

  // Fetch edge-latency devices (separate from Stargates). They can be assigned to a
  // region too, so they must be visible here — and a region can't be deleted while it
  // still has them.
  const fetchEdgeDevices = async () => {
    try {
      const response = await axios.get('/edge-devices');
      setEdgeDevices(response.data || []);
    } catch (err) {
      if (!isCanceled(err)) {
        console.error('Error fetching edge devices:', err);
      }
    }
  };

  // Edge devices belonging to a region, EXCLUDING ones that are really Stargates
  // (WG IP matches a Stargate) — those duplicate the Stargate and are surfaced on
  // the Edge Latency page, not here. Only genuinely non-Stargate edge devices count.
  const regionEdgeDevices = (regionId) => {
    const sgIps = new Set(
      (stargates || []).map((s) => (s.ip_address || '').trim()).filter(Boolean)
    );
    return edgeDevices.filter(
      (d) =>
        d.region_id === regionId && !((d.wg_ip || '').trim() && sgIps.has((d.wg_ip || '').trim()))
    );
  };

  const clientCountsFetched = useRef(false);
  const clientCountsInFlight = useRef(false);

  const fetchClientCounts = async (sgList) => {
    const onlineSgs = sgList.filter((sg) => isDeviceOnline(sg.health_status));
    if (onlineSgs.length === 0) {
      setClientCountsLoading(false);
      clientCountsFetched.current = true;
      return;
    }
    if (clientCountsInFlight.current) return;

    if (!clientCountsFetched.current) setClientCountsLoading(true);
    clientCountsInFlight.current = true;

    // ONE batch request (the backend fans out to the devices server-side) instead
    // of N slow per-device proxy calls. Metadata on a loaded Stargate is 5–8s;
    // firing N individual proxies starved the browser pool. Timeout matches the
    // Stargates page (45s) so a slow VLAN sidecar does not abort into a fake 0.
    try {
      const resp = await axios.post(
        '/proxy/network/batch-clients',
        {
          stargate_ids: onlineSgs.map((sg) => sg.id),
          interface: 'eth1',
        },
        { timeout: 45000, _skipRetry: true }
      );
      const batch = resp.data || {};
      const counts = {};
      for (const sg of onlineSgs) {
        const n = countBatchClients(batch[String(sg.id)]);
        if (n == null) continue;
        counts[sg.id] = n;
      }
      setClientCounts((prev) => {
        const next = { ...prev, ...counts };
        try {
          sessionStorage.setItem('regionClientCounts', JSON.stringify(next));
        } catch {
          /* ignore */
        }
        return next;
      });
    } catch (_) {
      // best-effort — leave prior counts in place on failure
    } finally {
      clientCountsInFlight.current = false;
      clientCountsFetched.current = true;
      setClientCountsLoading(false);
    }
  };

  useEffect(() => {
    fetchRegions();
    fetchAllStargates();
    fetchEdgeDevices();

    const interval = setInterval(() => {
      fetchAllStargates();
      fetchEdgeDevices();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (stargates.length > 0) {
      fetchClientCounts(stargates);
    }
  }, [stargates]);

  // Handle region click
  const handleRegionClick = (regionId) => {
    navigate(`/regions/${regionId}`);
  };

  // Open dialog for adding/editing region
  const handleOpenDialog = (region = null) => {
    if (region) {
      setEditingRegion(region);
      setRegionName(region.name);
      setCountryFlag(region.country_flag || '');
    } else {
      setEditingRegion(null);
      setRegionName('');
      setCountryFlag('');
    }
    setOpenDialog(true);
  };

  // Close dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingRegion(null);
    setRegionName('');
    setCountryFlag('');
  };

  // Save region
  const handleSaveRegion = async () => {
    // Require all fields filled
    if (!regionName.trim() || !countryFlag) {
      setError('All fields are required.');
      return;
    }

    // Prevent duplicate region names (case-insensitive, exclude self when editing)
    const nameToCheck = regionName.trim().toLowerCase();
    const duplicateName = regions.some(
      (r) =>
        r.name.trim().toLowerCase() === nameToCheck && (!editingRegion || r.id !== editingRegion.id)
    );
    if (duplicateName) {
      setError('A region with this name already exists. Please choose a different name.');
      return;
    }

    try {
      if (editingRegion) {
        // Update existing region
        await axios.put(`/regions/${editingRegion.id}`, {
          name: regionName,
          country_flag: countryFlag,
        });
      } else {
        // Create new region
        await axios.post('/regions', {
          name: regionName,
          country_flag: countryFlag,
        });
      }
      handleCloseDialog();
      fetchRegions();
    } catch (err) {
      // Handle backend validation errors
      if (err.response && err.response.data && err.response.data.detail) {
        setError(err.response.data.detail);
      } else {
        setError('Failed to save region. Please try again.');
      }
      console.error('Error saving region:', err);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (region, event) => {
    event.stopPropagation();
    setRegionToDelete(region);
    setDeleteConfirmOpen(true);
  };

  // Delete region
  const handleDeleteRegion = async () => {
    try {
      await axios.delete(`/regions/${regionToDelete.id}`);
      setDeleteConfirmOpen(false);
      setRegionToDelete(null);
      fetchRegions();
    } catch (err) {
      setError('Failed to delete region. Please try again.');
      console.error('Error deleting region:', err);
    }
  };

  // Batch deletion functions
  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setSelectAll(checked);
    if (checked) {
      setSelectedRegions(regions.map((region) => region.id));
    } else {
      setSelectedRegions([]);
    }
  };

  const handleSelectRegion = (regionId, event) => {
    event.stopPropagation();
    const selectedIndex = selectedRegions.indexOf(regionId);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = [...selectedRegions, regionId];
    } else {
      newSelected = selectedRegions.filter((id) => id !== regionId);
    }

    setSelectedRegions(newSelected);
    setSelectAll(newSelected.length === regions.length);
  };

  const handleBatchDelete = () => {
    if (selectedRegions.length === 0) return;
    setBatchDeleteConfirmOpen(true);
  };

  const handleBatchDeleteConfirm = async () => {
    try {
      // Delete all selected regions
      await Promise.all(selectedRegions.map((regionId) => axios.delete(`/regions/${regionId}`)));

      // Reset selection and close dialog
      setSelectedRegions([]);
      setSelectAll(false);
      setBatchDeleteConfirmOpen(false);

      // Refresh the regions list
      fetchRegions();
    } catch (err) {
      setError('Failed to delete some regions. Please try again.');
      console.error('Error batch deleting regions:', err);
    }
  };

  // Clear selections when regions change
  useEffect(() => {
    setSelectedRegions([]);
    setSelectAll(false);
  }, [regions]);

  const getRegionHealth = (regionId) => {
    // The Location bubble reflects STARGATE health only, so it stays consistent
    // with the "N online" Stargates text beside it. Edge-device health is shown
    // separately in the Edge Devices column (its own coloured dots).
    const regionStargates = stargates.filter((sg) => sg.region_id === regionId);
    if (regionStargates.length === 0) return 'warning';
    const hasRed = regionStargates.some(
      (sg) => !sg.health_status || sg.health_status === 'red' || sg.health_status === 'offline'
    );
    const hasYellow = regionStargates.some(
      (sg) => sg.health_status === 'yellow' || sg.health_status === 'degraded'
    );
    if (hasRed) return 'red';
    if (hasYellow) return 'warning';
    return 'green';
  };

  // Helper to get flag image URL
  const getFlagUrl = (countryCode) => {
    if (!countryCode) return null;

    // Check if countryCode is a string and has valid length
    if (typeof countryCode !== 'string' || countryCode.length !== 2) {
      console.warn('Invalid country code format:', countryCode);
      return null;
    }

    return `https://flagcdn.com/w40/${countryCode.toLowerCase()}.png`;
  };

  // Render flag image
  const renderFlag = (countryCode) => {
    const url = getFlagUrl(countryCode);
    if (!url) return <FlagIcon color="disabled" />;
    return (
      <img
        src={url}
        alt="flag"
        style={{
          width: 28,
          height: 20,
          borderRadius: 4,
          objectFit: 'cover',
          boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
        }}
      />
    );
  };

  return (
    <Box sx={pageRootSx}>
      <Box sx={pageHeaderSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 0 }}>
          <FlagIcon sx={{ fontSize: 32, color: 'text.primary', flexShrink: 0 }} />
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                color: 'text.primary',
                fontSize: { xs: '1.35rem', sm: undefined },
                wordBreak: 'break-word',
              }}
            >
              Geographical Regions
            </Typography>
          </Box>
        </Box>
        {isAdmin() && (
          <Box sx={pageHeaderActionsSx}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleOpenDialog()}
              sx={{
                backgroundColor: !isDarkMode ? '#1e4670' : undefined,
                '&:hover': {
                  backgroundColor: !isDarkMode ? '#2c5282' : undefined,
                },
              }}
            >
              Add Region
            </Button>
          </Box>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Batch Delete Controls */}
      {isAdmin() && selectedRegions.length > 0 && (
        <Box
          sx={{
            mb: 2,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <Chip label={`${selectedRegions.length} selected`} color="primary" variant="outlined" />
          <Button
            variant="contained"
            color="error"
            startIcon={<DeleteSweepIcon />}
            onClick={handleBatchDelete}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            Delete Selected ({selectedRegions.length})
          </Button>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : regions.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center' }}>
          <Typography variant="body1">
            No geographical regions found. {isAdmin() && 'Click "Add Region" to create one.'}
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 2,
            ...wideTableContainerSx,
            maxHeight: 'calc(100vh - 250px)', // Limit max height to available viewport
            boxShadow: isDarkMode
              ? '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
              : '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <Table stickyHeader sx={wideTableSx(960)}>
            <TableHead>
              <TableRow>
                {isAdmin() && (
                  <TableCell padding="checkbox">
                    <Checkbox
                      color="primary"
                      indeterminate={
                        selectedRegions.length > 0 && selectedRegions.length < regions.length
                      }
                      checked={regions.length > 0 && selectAll}
                      onChange={handleSelectAll}
                      inputProps={{ 'aria-label': 'select all regions' }}
                    />
                  </TableCell>
                )}
                <TableCell>Location</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Stargates</TableCell>
                <TableCell>Edge Devices</TableCell>
                <TableCell>Clients</TableCell>
                {isAdmin() && <TableCell align="right">Actions</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {regions.map((region) => (
                <TableRow
                  key={region.id}
                  hover
                  onClick={() => handleRegionClick(region.id)}
                  sx={{ cursor: 'pointer' }}
                  selected={selectedRegions.indexOf(region.id) !== -1}
                >
                  {isAdmin() && (
                    <TableCell
                      padding="checkbox"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleSelectRegion(region.id, event);
                      }}
                      sx={{ cursor: 'pointer' }}
                    >
                      <Checkbox
                        color="primary"
                        checked={selectedRegions.indexOf(region.id) !== -1}
                        onChange={(event) => handleSelectRegion(region.id, event)}
                        inputProps={{
                          'aria-labelledby': `checkbox-list-label-${region.id}`,
                        }}
                      />
                    </TableCell>
                  )}
                  <TableCell>
                    {/* Region health indicator bubble and flag, vertically centered */}
                    <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                      <span
                        className={`health-indicator ${getRegionHealth(region.id) === 'green' ? 'health-green pulse-green' : getRegionHealth(region.id) === 'warning' ? 'health-warning pulse-warning' : 'health-red pulse-red'}`}
                        style={{ marginRight: 8 }}
                      ></span>
                      {renderFlag(region.country_flag)}
                    </span>
                  </TableCell>
                  <TableCell>{region.name}</TableCell>
                  <TableCell>
                    {/* Live health summary for stargates in this region */}
                    {(() => {
                      const regionStargates = stargates.filter((sg) => sg.region_id === region.id);
                      const green = regionStargates.filter(
                        (sg) => sg.health_status === 'green'
                      ).length;
                      const yellow = regionStargates.filter(
                        (sg) => sg.health_status === 'yellow' || sg.health_status === 'degraded'
                      ).length;
                      const offline = regionStargates.length - green - yellow;
                      return (
                        <span>
                          {green > 0 && (
                            <span style={{ color: '#4caf50', fontWeight: 600 }}>
                              ● {green} online
                            </span>
                          )}
                          {yellow > 0 && (
                            <span
                              style={{
                                marginLeft: green > 0 ? 10 : 0,
                                color: '#ff9800',
                                fontWeight: 600,
                              }}
                            >
                              ● {yellow} degraded
                            </span>
                          )}
                          {offline > 0 && (
                            <span
                              style={{
                                marginLeft: green > 0 || yellow > 0 ? 10 : 0,
                                color: '#f44336',
                                fontWeight: 600,
                              }}
                            >
                              ● {offline} offline
                            </span>
                          )}
                          {green === 0 && yellow === 0 && offline === 0 && (
                            <span style={{ color: '#888' }}>No devices</span>
                          )}
                        </span>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {/* Edge-latency devices in this region — same labelled style as
                        the Stargates column so the status reads plainly. */}
                    {(() => {
                      const eds = regionEdgeDevices(region.id);
                      if (eds.length === 0) {
                        return (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            —
                          </Typography>
                        );
                      }
                      const green = eds.filter((d) => d.health_status === 'green').length;
                      const yellow = eds.filter(
                        (d) => d.health_status === 'yellow' || d.health_status === 'degraded'
                      ).length;
                      const offline = eds.length - green - yellow;
                      return (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Tooltip title="Edge latency-probe devices in this region">
                            <DeveloperBoardIcon
                              fontSize="small"
                              sx={{ color: 'text.secondary', fontSize: 18 }}
                            />
                          </Tooltip>
                          <span>
                            {green > 0 && (
                              <span style={{ color: '#4caf50', fontWeight: 600 }}>
                                ● {green} online
                              </span>
                            )}
                            {yellow > 0 && (
                              <span
                                style={{
                                  marginLeft: green > 0 ? 10 : 0,
                                  color: '#ff9800',
                                  fontWeight: 600,
                                }}
                              >
                                ● {yellow} degraded
                              </span>
                            )}
                            {offline > 0 && (
                              <span
                                style={{
                                  marginLeft: green > 0 || yellow > 0 ? 10 : 0,
                                  color: '#f44336',
                                  fontWeight: 600,
                                }}
                              >
                                ● {offline} offline
                              </span>
                            )}
                          </span>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  <TableCell>
                    {(() => {
                      const regionStargates = stargates.filter((sg) => sg.region_id === region.id);
                      const total = regionStargates.reduce(
                        (sum, sg) => sum + (parseInt(clientCounts[sg.id]) || 0),
                        0
                      );
                      const hasCount = regionStargates.some((sg) => clientCounts[sg.id] != null);
                      if (clientCountsLoading && !hasCount) {
                        return <CircularProgress size={16} thickness={4} />;
                      }
                      if (!hasCount) {
                        return (
                          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                            {'\u2014'}
                          </Typography>
                        );
                      }
                      return (
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <PeopleIcon
                            fontSize="small"
                            sx={{ color: 'text.secondary', fontSize: 18 }}
                          />
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {total}
                          </Typography>
                        </Box>
                      );
                    })()}
                  </TableCell>
                  {isAdmin() && (
                    <TableCell align="right">
                      <Tooltip title="Edit Region">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenDialog(region);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Region">
                        <IconButton size="small" onClick={(e) => handleDeleteClick(region, e)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      {/* Add/Edit Region Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingRegion ? 'Edit Region' : 'Add New Region'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Region Name"
            fullWidth
            required
            variant="outlined"
            value={regionName}
            onChange={(e) => setRegionName(e.target.value)}
          />
          <Box sx={{ mt: 2 }}>
            <Select
              value={countryFlag}
              onChange={(e) => setCountryFlag(e.target.value)}
              displayEmpty
              fullWidth
              variant="outlined"
              renderValue={(selected) => {
                if (!selected) return <span style={{ color: '#888' }}>Select Location</span>;
                const country = countryFlags.find((c) => c.code === selected);
                const flagUrl = getFlagUrl(selected);
                return (
                  <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    {flagUrl && (
                      <img
                        src={flagUrl}
                        alt="flag"
                        style={{
                          width: 24,
                          height: 16,
                          borderRadius: 3,
                          objectFit: 'cover',
                          boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                        }}
                      />
                    )}
                    <span style={{ fontWeight: 600, width: 32 }}>{selected.toUpperCase()}</span>
                    <span>{country?.name}</span>
                  </span>
                );
              }}
            >
              <MenuItem value="">
                <ListItemText primary={<span style={{ color: '#888' }}>Select Location</span>} />
              </MenuItem>
              {countryFlags.map((country) => {
                const flagUrl = getFlagUrl(country.code);
                return (
                  <MenuItem key={country.code} value={country.code}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      {flagUrl && (
                        <img
                          src={flagUrl}
                          alt="flag"
                          style={{
                            width: 24,
                            height: 16,
                            borderRadius: 3,
                            objectFit: 'cover',
                            boxShadow: '0 1px 4px rgba(0,0,0,0.10)',
                          }}
                        />
                      )}
                      <span style={{ fontWeight: 600, width: 32 }}>
                        {country.code.toUpperCase()}
                      </span>
                      <span>{country.name}</span>
                    </span>
                  </MenuItem>
                );
              })}
            </Select>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveRegion} variant="contained" disabled={!regionName.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          {(() => {
            const sgCount = regionToDelete
              ? stargates.filter((sg) => sg.region_id === regionToDelete.id).length
              : 0;
            const edCount = regionToDelete ? regionEdgeDevices(regionToDelete.id).length : 0;
            const parts = [];
            if (sgCount > 0) parts.push(`${sgCount} Stargate${sgCount === 1 ? '' : 's'}`);
            if (edCount > 0) parts.push(`${edCount} edge device${edCount === 1 ? '' : 's'}`);
            return (
              <Typography>
                Are you sure you want to delete the region "{regionToDelete?.name}"?
                {parts.length > 0
                  ? ` This will also permanently delete the ${parts.join(' and ')} in this region.`
                  : ' This region has no devices.'}
              </Typography>
            );
          })()}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDeleteRegion} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Batch Delete Confirmation Dialog */}
      <Dialog
        open={batchDeleteConfirmOpen}
        onClose={() => setBatchDeleteConfirmOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>⚠️ Confirm Batch Deletion</DialogTitle>
        <DialogContent>
          <Typography variant="h6" gutterBottom sx={{ color: 'error.main' }}>
            Are you sure you want to delete {selectedRegions.length} selected regions?
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, mb: 2 }}>
            This action will permanently delete:
          </Typography>
          <Box sx={{ ml: 2, mb: 2 }}>
            {selectedRegions.map((regionId) => {
              const region = regions.find((r) => r.id === regionId);
              return (
                <Typography
                  key={regionId}
                  variant="body2"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    mb: 0.5,
                  }}
                >
                  • {renderFlag(region?.country_flag)} {region?.name}
                </Typography>
              );
            })}
          </Box>
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Warning:</strong> This will also delete all Stargates and edge devices in
              these regions. This action cannot be undone.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={handleBatchDeleteConfirm}
            color="error"
            variant="contained"
            startIcon={<DeleteSweepIcon />}
          >
            Yes, Delete All {selectedRegions.length} Regions
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const RegionsPage = () => <RegionsPageInner />;

export default RegionsPage;
